import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  Title2,
  makeStyles,
} from '@fluentui/react-components';
import { ACCESS_CODE_LENGTH, normalizeAccessCode } from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import { openSession, type PortalError } from './api.js';

const useStyles = makeStyles({
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  code: {
    // 16px minimum, or iOS Safari zooms the whole page on focus and the person loses the
    // rest of the form off-screen.
    fontSize: tokens.fontSizeBase500,
    letterSpacing: tokens.spacingHorizontalXS,
    textTransform: 'uppercase',
  },
  submit: { minHeight: '2.75rem' },
});

export interface TokenExchangeProps {
  readonly onOpened: () => void;
  /** `token` is handed up only so the ended page can ask for a fresh link with it (#82). */
  readonly onUnrecoverable: (error: PortalError, token: string | null) => void;
}

/**
 * Turn a link and a code into a session.
 *
 * The token is read from `?t=` ONCE and the URL is rewritten immediately. After that it
 * lives only in a ref for the length of this component. It must not survive in the address
 * bar, in browser history, or in a screenshot the candidate sends someone — all three are
 * places a link that still works would sit indefinitely.
 */
export function TokenExchange({ onOpened, onUnrecoverable }: TokenExchangeProps) {
  const styles = useStyles();
  const token = useRef<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<PortalError | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const found = new URLSearchParams(window.location.search).get('t');
    if (found !== null) {
      token.current = found;
      // Strip it before anything else can observe it — including a screenshot.
      window.history.replaceState(null, '', '/portal');
    }
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (token.current === null) {
      onUnrecoverable({
        status: 401,
        message: 'This link is not valid. Ask us for a new one.',
        canReissue: true,
      }, null);
      return;
    }

    setBusy(true);
    const failed = await openSession(token.current, normalizeAccessCode(code));
    setBusy(false);

    if (failed === null) {
      onOpened();
      return;
    }

    // The portal is closed on this server (#625): no code can open it, so the form goes away.
    if (failed.unavailable) {
      onUnrecoverable(failed, null);
      return;
    }

    // An expired or locked link cannot be recovered by retyping the code, so it leaves this
    // screen. A wrong code stays, and the message never says which of the two was wrong.
    if (failed.status === 429) {
      // Locked by wrong codes: HR's, never self-service (#82 review). A reissue keeps the code,
      // so offering one here would hand a guesser a fresh set of tries.
      onUnrecoverable({ ...failed, canReissue: false }, null);
      return;
    }
    if (failed.message.includes('expired')) {
      onUnrecoverable(failed, token.current);
      return;
    }
    setError(failed);
    setCode('');
  }

  const ready = normalizeAccessCode(code).length === ACCESS_CODE_LENGTH;

  return (
    <form className={styles.form} onSubmit={submit}>
      <div>
        <Title2 as="h1">Your onboarding</Title2>
        <br />
        <Text>Enter the code we sent you separately from this link.</Text>
      </div>

      {error === null ? null : (
        <MessageBar intent="error">
          <MessageBarBody>{error.message}</MessageBarBody>
        </MessageBar>
      )}

      <Field label="Access code" hint={`${ACCESS_CODE_LENGTH} characters. Spaces and dashes are fine.`}>
        <Input
          className={styles.code}
          value={code}
          onChange={(_, d) => setCode(d.value)}
          // `one-time-code` gets iOS and Android to offer it from the message it arrived in.
          autoComplete="one-time-code"
          inputMode="text"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          disabled={busy}
        />
      </Field>

      <Button appearance="primary" type="submit" className={styles.submit} disabled={!ready || busy}>
        {busy ? <Spinner size="tiny" label="Checking" labelPosition="after" /> : 'Continue'}
      </Button>
    </form>
  );
}
